import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from '@/apps/common/settings/redux/configurationSlice';
import { ConfigRowItem } from '@/apps/common/settings/application/health_check/ConfigInputRow';
import { axiosBaseQuery } from '@/redux/axios';
import {
  HealthCheckConfigurationItem,
  UpdateHealthCheckConfigPayload,
} from '@/apps/health_check/types/IHealthCheck';
import {
  createNotificationHandler,
  SCOPES,
  MESSAGES,
} from '@/redux/notificationHandler';
import { apiRoutes } from '@/api/apiRoutes';
import {
  UpdateTemperatureCheckpointsPayload,
  GetHealthCheckConfigurationsParams,
} from '@/api/types';

export const healthCheckConfigurationsApi = createApi({
  reducerPath: 'healthCheckConfigurationsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['HealthCheckConfigurations'],
  endpoints: (builder) => ({
    getHealthCheckConfigurations: builder.query<
      HealthCheckConfigurationItem[],
      GetHealthCheckConfigurationsParams
    >({
      query: (params) => ({
        url: apiRoutes.getHealthCheckConfigurations.path(params),
        method: apiRoutes.getHealthCheckConfigurations.method,
      }),
      providesTags: ['HealthCheckConfigurations'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
        MESSAGES.LOAD,
        false
      ),
    }),
    updateHealthCheckConfiguration: builder.mutation<
      HealthCheckConfigurationItem,
      { id: number | string } & UpdateHealthCheckConfigPayload
    >({
      query: ({ id, ...patch }) => ({
        url: apiRoutes.updateHealthCheckConfigurationById.path(id),
        method: apiRoutes.updateHealthCheckConfigurationById.method,
        data: patch,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
          MESSAGES.UPDATE
        )({ id, ...patch }, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;

        dispatch(
          healthCheckConfigurationsApi.util.updateQueryData(
            'getHealthCheckConfigurations',
            { contractId: updatedItem.contractId },
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft[idx] = updatedItem;
              }
            }
          )
        );
      },
    }),
    updateTemperatureCheckpoints: builder.mutation<
      ConfigRowItem,
      UpdateTemperatureCheckpointsPayload
    >({
      query: (data) => ({
        url: apiRoutes.updateTemperatureCheckpoints.path(),
        method: apiRoutes.updateTemperatureCheckpoints.method,
        data: data,
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_HEALTH_CHECK_CONFIG,
          MESSAGES.UPDATE
        )(data, { dispatch, queryFulfilled });

        await queryFulfilled;

        dispatch(
          healthCheckConfigurationsApi.util.updateQueryData(
            'getHealthCheckConfigurations',
            { contractId: data.contractId },
            (draft) => {
              const idx = draft.findIndex(
                (item) => item.type === 5 && item.severity === data.severity
              );
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], active: data.isActive };
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetHealthCheckConfigurationsQuery,
  useUpdateHealthCheckConfigurationMutation,
  useUpdateTemperatureCheckpointsMutation,
} = healthCheckConfigurationsApi;

export default healthCheckConfigurationsApi;
